import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as yup from "yup";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys, onboardingKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import jamf_protect_copy_api_password from "./images/jamf_protect_copy_api_password.png";
import jamf_protect_copy_the_client_id from "./images/jamf_protect_copy_the_client_id.png";
import jamf_protect_create_api_client from "./images/jamf_protect_create_api_client.png";
import jamf_protect_create_role from "./images/jamf_protect_create_role.png";

const JamfProtectIntegration = () => {
  const [showSecret, setShowSecret] = useState(false);
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const handleClickShowSecret = () => setShowSecret(!showSecret);
  const handleMouseDownSecret = () => setShowSecret(!showSecret);
  const formInitialValues = {
    url: "",
    client_secret: "",
    client_id: "",
  };
  const formCheckoutSchema = yup.object().shape({
    url: yup.string().required("required"),
    client_secret: yup.string().required("required"),
    client_id: yup.string().required("required"),
  });
  const handleFormSubmit = (values) => {
    onSubmit(values);
  };
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("jamf_protect");
  const [loading, setLoading] = useState(false);
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerJamfProtectIntegration } = useAPI();
  const queryClient = useQueryClient();

  const onSubmit = (values) => {
    setLoading(true);
    registerJamfProtectIntegration(
      values.url.trim(),
      values.client_secret.trim(),
      values.client_id.trim()
    )
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        // only in EDR integrations, we need to invalidate the onboarding context
        queryClient.invalidateQueries({
          queryKey: onboardingKeys.all,
        });
        Toast.success("Jamf Protect integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically read the configurations from Jamf Protect and prioritize the suggested remediation."
            : ""
        }
        image={logos.JamfProtectLogo}
        iconBorderRadius={0}
        name={"Jamf Protect"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>
                Jamf Protect is integrated!
              </Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              {/* SETUP INSTRUCTIONS */}
              <Box>
                <Formik
                  onSubmit={handleFormSubmit}
                  initialValues={formInitialValues}
                  validationSchema={formCheckoutSchema}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <GuidelineStepByStep>
                        <GuidelineStep title="Login to your Jamf Protect console">
                          <Typography variant={"body2"}>
                            Enter below your Tenant name in Jamf Protect
                            console.
                          </Typography>
                          <Typography variant={"body2"}>
                            Your URL should look like this:{" "}
                          </Typography>
                          <Typography variant={"body2"}>
                            https://<strong>tenant-name</strong>
                            .protect.jamfcloud.com
                          </Typography>
                          <Grid container alignItems="center" spacing={1}>
                            <Grid item>
                              <Typography variant="body1">https://</Typography>
                            </Grid>
                            <Grid item>
                              <TextField
                                fullWidth
                                variant="filled"
                                name="url"
                                label="Tenant name"
                                value={values.url}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={!!touched.url && !!errors.url}
                                helperText={touched.url && errors.url}
                                disabled={guideMode}
                              />
                            </Grid>
                            <Grid item>
                              <Typography variant="body1">
                                .protect.jamfcloud.com
                              </Typography>
                            </Grid>
                          </Grid>
                          <PrimaryButton
                            onClick={() => {
                              window.open(
                                `https://${values.url}.protect.jamfcloud.com`,
                                "_blank"
                              );
                            }}
                            style={{ textTransform: "none" }}
                            disabled={!values.url}
                          >
                            Login to Jamf Protect
                          </PrimaryButton>
                        </GuidelineStep>
                        <GuidelineStep title="Create a custom read-only role">
                          <Typography variant={"body2"}>
                            In Jamf Protect console.
                          </Typography>
                          <Typography variant={"body2"}>
                            Click Administrative {">"} Account.
                          </Typography>
                          <Typography variant={"body2"}>
                            Click the Roles tab.
                          </Typography>
                          <Typography variant={"body2"}>
                            Click Create Role.
                          </Typography>
                          <Typography variant={"body2"}>
                            Name your role, for example{" "}
                            <b>Breeze Integration</b>.
                          </Typography>
                          <Typography variant={"body2"}>
                            Configure permissions for your role <b>Read All</b>.
                          </Typography>
                          <Typography variant={"body2"}>Click save.</Typography>
                          <ScreenshotAccordion
                            images={[
                              {
                                file: jamf_protect_create_role,
                                altext: "Jamf Protect create role",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                        </GuidelineStep>
                        <GuidelineStep title="Create a new API token">
                          <Typography variant={"body2"}>
                            In Jamf Protect console.
                          </Typography>
                          <Typography variant={"body2"}>
                            Click Administrative {">"} API Clients.
                          </Typography>
                          <Typography variant={"body2"}>
                            Click Create API Client.
                          </Typography>
                          <Typography variant={"body2"}>
                            Name your API client, for example{" "}
                            <b>Breeze-security</b>.
                          </Typography>
                          <Typography variant={"body2"}>
                            Assign for the API client the role you created
                            previously.
                          </Typography>
                          <Typography variant={"body2"}>Click Save.</Typography>
                          <ScreenshotAccordion
                            images={[
                              {
                                file: jamf_protect_create_api_client,
                                altext: "Jamf Protect create API client",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                          <Typography variant={"body2"}>
                            A pop up with the client secret will appear.
                          </Typography>
                          <Typography variant={"body2"}>
                            Click on <b>Copy to Clipboard</b> to copy the client
                            secret. and paste it below.
                          </Typography>
                          <ScreenshotAccordion
                            images={[
                              {
                                file: jamf_protect_copy_api_password,
                                altext: "Jamf Protect copy API password",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                          >
                            <TextField
                              fullWidth
                              variant="filled"
                              name="client_secret"
                              label="API client password"
                              value={values.client_secret}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              autoComplete="off"
                              type={showSecret ? "text" : "password"}
                              error={
                                !!touched.client_secret &&
                                !!errors.client_secret
                              }
                              helperText={
                                touched.client_secret && errors.client_secret
                              }
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      aria-label="toggle password visibility"
                                      onClick={handleClickShowSecret}
                                      onMouseDown={handleMouseDownSecret}
                                    >
                                      {showSecret ? (
                                        <Visibility />
                                      ) : (
                                        <VisibilityOff />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                              style={{ width: "700px" }}
                              disabled={guideMode}
                            />
                          </Box>
                        </GuidelineStep>

                        <GuidelineStep title="Copy the Client ID">
                          <Typography variant={"body2"}>
                            You are now in the page of the client API you
                            created.
                          </Typography>
                          <Typography variant={"body2"}>
                            Copy the Client ID and paste it below.
                          </Typography>
                          <ScreenshotAccordion
                            images={[
                              {
                                file: jamf_protect_copy_the_client_id,
                                altext: "Jamf Protect copy the client ID",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                          >
                            <TextField
                              fullWidth
                              variant="filled"
                              name="client_id"
                              label="Client ID"
                              value={values.client_id}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              autoComplete="off"
                              error={!!touched.client_id && !!errors.client_id}
                              helperText={touched.client_id && errors.client_id}
                              style={{ width: "700px" }}
                              disabled={guideMode}
                            />
                            <Box mt={2}>
                              <PrimaryButton type="submit" disabled={guideMode}>
                                Connect
                              </PrimaryButton>
                            </Box>
                          </Box>
                        </GuidelineStep>
                      </GuidelineStepByStep>
                    </Form>
                  )}
                </Formik>
              </Box>
            </div>
          )
        }
      />
    </Box>
  );
};

export default JamfProtectIntegration;
